import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import { Snackbar } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Banner, SplitButton } from "../components/m3";
import useCircularityHtmlReport from "../hooks/useCircularityHtmlReport";

/**
 * Circularity report actions: the main button opens the in-app report tab,
 * the menu downloads the offline interactive HTML report.
 *
 * @param {boolean} active - the in-app report tab is open
 * @param {function} onOpenReport
 * @param {object} reportInputs - { entity, topic, combined_data, pciData, boundaryState, serviceLife }
 */
const CircularityReportButton = ({ active, onOpenReport, reportInputs }) => {
  const { t } = useTranslation();
  const htmlReport = useCircularityHtmlReport(reportInputs);

  const options = useMemo(
    () => [
      {
        key: "in-app-report",
        label: t("Circularity Report"),
        description: t("Open the report in this workspace"),
        icon: <SummarizeOutlinedIcon sx={{ color: "var(--m3-primary)", fontSize: 18 }} />,
        onClick: onOpenReport,
      },
      {
        key: "html-report",
        label: t("Interactive HTML report"),
        description: htmlReport.isGenerating ? t("Generating report…") : t("Offline report with interactive charts and tables"),
        icon: <InsightsOutlinedIcon sx={{ color: "var(--m3-primary)", fontSize: 18 }} />,
        onClick: htmlReport.generate,
        disabled: htmlReport.isGenerating,
      },
    ],
    [htmlReport.generate, htmlReport.isGenerating, onOpenReport, t]
  );

  return (
    <>
      <SplitButton
        label={t("Circularity Report")}
        icon={<SummarizeOutlinedIcon />}
        onClick={onOpenReport}
        options={options}
        variant={active ? "filled" : "outlined"}
        menuLabel={t("More report options")}
      />
      <Snackbar open={Boolean(htmlReport.error)} autoHideDuration={6000} onClose={htmlReport.clearError} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <div>
          <Banner tone="error" onDismiss={htmlReport.clearError}>
            {htmlReport.error}
          </Banner>
        </div>
      </Snackbar>
    </>
  );
};

export default CircularityReportButton;
